import React, { useState, useRef } from 'react';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../context/LanguageContext';
import { useFormspark } from '@formspark/use-formspark';
import { PHONE_COUNTRY_OPTIONS } from '../../../constants/phoneCountries';
import { sanitizePhoneNumber } from '../../../utils/phone';
import './Hero.css';

// reCAPTCHA v2 types
declare global {
  interface Window {
    grecaptcha: any;
  }
}

const Hero: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    countryCode: '+31',
    phone: '',
    email: '',
    message: ''
  });
  
  // reCAPTCHA v2 ref and state
  const recaptchaRef = useRef<HTMLDivElement>(null);
  const recaptchaRenderTimerRef = useRef<number | null>(null);
  const isRecaptchaRenderingRef = useRef(false);
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState<number | null>(null);

  // Get configuration from environment variables
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const formsparkFormId = process.env.REACT_APP_FORMSPARK_HERO_ID;
  const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';
  
  // Initialize Formspark hook with fallback
  const [submit, submitting] = useFormspark({
    formId: (formsparkFormId && !isDevMode) ? formsparkFormId : 'dev-mode-placeholder'
  });

  // Initialize reCAPTCHA v2 widget
  React.useEffect(() => {
    const initializeReCaptcha = () => {
      if (!window.grecaptcha || !recaptchaSiteKey || !recaptchaRef.current || recaptchaWidgetId !== null || isRecaptchaRenderingRef.current) {
        return;
      }

      const hostElement = recaptchaRef.current;
      if (hostElement.dataset.recaptchaRendered === 'true' || hostElement.childElementCount > 0) {
        return;
      }

      console.log('Initializing Hero reCAPTCHA v2 widget...');
      isRecaptchaRenderingRef.current = true;

      try {
        recaptchaRenderTimerRef.current = window.setTimeout(() => {
          if (!recaptchaRef.current || recaptchaWidgetId !== null) {
            isRecaptchaRenderingRef.current = false;
            return;
          }

          const currentHostElement = recaptchaRef.current;
          if (currentHostElement.dataset.recaptchaRendered === 'true' || currentHostElement.childElementCount > 0) {
            isRecaptchaRenderingRef.current = false;
            return;
          }

          const widgetId = window.grecaptcha.render(currentHostElement, {
            sitekey: recaptchaSiteKey,
            theme: 'light',
            size: 'normal',
            callback: () => {
              console.log('Hero reCAPTCHA completed successfully!');
              const container = recaptchaRef.current?.closest('.recaptcha-container');
              if (container) {
                container.classList.remove('required');
                container.classList.add('completed');
              }
            },
            'expired-callback': () => {
              console.log('Hero reCAPTCHA expired, user needs to solve again');
              const container = recaptchaRef.current?.closest('.recaptcha-container');
              if (container) {
                container.classList.remove('completed');
                container.classList.add('required');
              }
            }
          });

          currentHostElement.dataset.recaptchaRendered = 'true';
          setRecaptchaWidgetId(widgetId);
          isRecaptchaRenderingRef.current = false;
          console.log('Hero reCAPTCHA v2 widget initialized successfully with ID:', widgetId);
        }, 100);
      } catch (error) {
        console.error('Failed to initialize Hero reCAPTCHA v2 widget:', error);
        isRecaptchaRenderingRef.current = false;
      }
    };

    if (window.grecaptcha && window.grecaptcha.ready) {
      window.grecaptcha.ready(initializeReCaptcha);
    } else if (!window.grecaptcha) {
      const timer = setTimeout(initializeReCaptcha, 1000);
      return () => {
        clearTimeout(timer);
        if (recaptchaRenderTimerRef.current !== null) {
          clearTimeout(recaptchaRenderTimerRef.current);
          recaptchaRenderTimerRef.current = null;
        }
      };
    }

    return () => {
      if (recaptchaRenderTimerRef.current !== null) {
        clearTimeout(recaptchaRenderTimerRef.current);
        recaptchaRenderTimerRef.current = null;
      }
    };
  }, [recaptchaSiteKey, recaptchaWidgetId]);

  // reCAPTCHA is now loaded globally in App.tsx

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'phone' ? sanitizePhoneNumber(value) : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      console.log('=== HERO FORM SUBMISSION START ===');
      console.log('Starting Hero form submission...');
      console.log('Form data:', formData);
      console.log('Environment config:', {
        recaptchaSiteKey: recaptchaSiteKey ? '✓ Available' : '✗ Missing',
        formsparkFormId: formsparkFormId ? '✓ Available' : '✗ Missing',
        isDevMode
      });
      
      // Check if required environment variables are set
      if (!formsparkFormId && !isDevMode) {
        throw new Error('Form yapılandırması eksik. Lütfen site yöneticisi ile iletişime geçin.');
      }

      if (formData.phone.length < 8 || formData.phone.length > 12) {
        throw new Error(t('form.phoneValidation'));
      }
      
      let recaptchaToken = '';
      
      // Try to get reCAPTCHA v2 token if configured
      if (recaptchaSiteKey && window.grecaptcha && recaptchaWidgetId !== null) {
        try {
          console.log('Getting reCAPTCHA v2 token...');
          console.log('reCAPTCHA widget ID:', recaptchaWidgetId);
          recaptchaToken = window.grecaptcha.getResponse(recaptchaWidgetId);
          console.log('reCAPTCHA v2 token received:', recaptchaToken ? 'Yes' : 'No');
          
          if (!recaptchaToken) {
            // Instead of alert, focus on reCAPTCHA widget
            const container = recaptchaRef.current?.closest('.recaptcha-container');
            if (container) {
              container.classList.add('required');
              container.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
          }
        } catch (recaptchaError) {
          console.error('reCAPTCHA error:', recaptchaError);
          // Focus on reCAPTCHA instead of showing alert
          const container = recaptchaRef.current?.closest('.recaptcha-container');
          if (container) {
            container.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return;
        }
      } else {
        console.warn('reCAPTCHA configuration:', {
          siteKey: recaptchaSiteKey ? 'Available' : 'Missing',
          grecaptcha: !!window.grecaptcha,
          widgetId: recaptchaWidgetId
        });
        if (recaptchaSiteKey) {
          // If reCAPTCHA is configured but not loaded, wait or show error in console
          if (window.grecaptcha && !recaptchaWidgetId) {
            console.warn('reCAPTCHA henüz yüklenmedi. Lütfen birkaç saniye bekleyin ve tekrar deneyin.');
            return;
          } else {
            console.error('reCAPTCHA doğrulaması yüklenemedi. Lütfen sayfayı yenileyin ve tekrar deneyin.');
            return;
          }
        }
        console.warn('reCAPTCHA not configured, submitting without it');
      }
      
      console.log('Submitting to Formspark using official hook...');
      console.log('Formspark Form ID:', formsparkFormId);
      
      // Check if we're in development mode
      console.log('Development mode:', isDevMode);
      
      if (isDevMode) {
        console.log('Running in development mode - simulating Hero form submission');
        console.log('Note: Formspark will work in production');
        // Simulate successful submission for local development
        await new Promise(resolve => setTimeout(resolve, 1000));
        console.log('Hero form submitted successfully! (simulated)');
        
        // Clear form and reset reCAPTCHA
        setFormData({ fullName: '', countryCode: '+31', phone: '', email: '', message: '' });
        if (window.grecaptcha && recaptchaWidgetId !== null) {
          window.grecaptcha.reset(recaptchaWidgetId);
        }
        
        console.log('Development mode: Form cleared and reCAPTCHA reset');
        // Redirect to thank you page
        navigate('/thank-you');
        return;
      }
      
      const formattedPhone = `${formData.countryCode} ${formData.phone}`;

      // Prepare data for Formspark hook
      const submissionData = {
        name: formData.fullName,
        fullName: formData.fullName,
        email: formData.email,
        phone: formattedPhone,
        countryCode: formData.countryCode,
        localPhone: formData.phone,
        message: formData.message,
        _subject: 'Hero Form - Rhinoplasty Consultation Request',
        _source: 'Hero Section Form',
        _formType: 'hero-consultation',
        ...(recaptchaToken && { 'g-recaptcha-response': recaptchaToken })
      };
      
      console.log('Submission data:', submissionData);
      
      // Submit using Formspark hook
      let result;
      try {
        result = await submit(submissionData);
        console.log('=== FORMSPARK HOOK RESULT ===');
        console.log('Submission result:', result);
      } catch (submissionError: any) {
        console.error('Formspark submission error:', submissionError);
        
        // Handle specific JSON parsing errors
        if (submissionError.message && submissionError.message.includes('Unexpected token')) {
          console.error('Server returned non-JSON response (likely HTML error page)');
          throw new Error('Sunucu hatası: Form gönderimi başarısız oldu. Lütfen daha sonra tekrar deneyin.');
        }
        
        // Handle network errors
        if (submissionError.message && submissionError.message.includes('Failed to fetch')) {
          throw new Error('Ağ bağlantısı hatası. İnternet bağlantınızı kontrol edin.');
        }
        
        // Re-throw the original error if it's something else
        throw submissionError;
      }
      
      // Success handling
      console.log('Hero form submitted successfully!');
      console.log('SUCCESS: Hero form submission completed!');
      
      // Clear form and reset reCAPTCHA
      setFormData({ fullName: '', countryCode: '+31', phone: '', email: '', message: '' });
      if (window.grecaptcha && recaptchaWidgetId !== null) {
        window.grecaptcha.reset(recaptchaWidgetId);
      }
      
      console.log('Form cleared and reCAPTCHA reset');
      console.log('=== HERO FORM SUBMISSION SUCCESS ===');
      
      // Redirect to thank you page
      navigate('/thank-you');
      
    } catch (error) {
      console.log('=== HERO FORM SUBMISSION ERROR ===');
      console.error('Hero form submission error:', error);
      console.error('Form data:', formData);
      console.error('reCAPTCHA available:', !!window.grecaptcha);
      console.error('Environment variables:', {
        recaptchaSiteKey: recaptchaSiteKey ? 'Set' : 'Not set',
        formsparkFormId: formsparkFormId ? 'Set' : 'Not set'
      });
      
      // Reset reCAPTCHA on error
      if (window.grecaptcha && recaptchaWidgetId !== null) {
        window.grecaptcha.reset(recaptchaWidgetId);
      }
      
      // More detailed error message based on error type
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        console.error('Network error detected');
      } else if (error instanceof Error) {
        console.error('Error message:', error.message);
        if (error.message.includes('CORS')) {
          console.error('Güvenlik hatası. Lütfen sayfayı yenileyin ve tekrar deneyin.');
        } else if (error.message.includes('timeout')) {
          console.error('İstek zaman aşımına uğradı. Lütfen tekrar deneyin.');
        } else if (error.message.includes('environment')) {
          console.error('Yapılandırma hatası. Lütfen site yöneticisi ile iletişime geçin.');
        } else {
          console.error(`Form gönderilirken hata oluştu: ${error.message}`);
        }
      } else {
        console.error('Form gönderilirken hata oluştu. Lütfen tekrar deneyin.');
      }
    }
  };

  return (
    <section id="home" className="hero-section">
      <Container>
        <Row className="align-items-center min-vh-100">
          {/* Contact Form - Sol (Desktop'ta sol, mobilde sonra) */}
          <Col lg={6} className="order-2 order-lg-1 hero-form-col">
            <div className="hero-form-container">
              <div className="form-header">
                <h3>{t('hero.form.title')}</h3>
                <p>{t('hero.form.subtitle')}</p>
              </div>
              
              <Form className="hero-contact-form" onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="fullName">{t('hero.form.name')} *</Form.Label>
                      <Form.Control
                        type="text"
                        id="fullName"
                        name="fullName"
                        placeholder={t('hero.form.name')}
                        value={formData.fullName}
                        onChange={handleInputChange}
                        required
                        className="form-control-hero"
                        autoComplete="name"
                        style={{ pointerEvents: 'auto', zIndex: 10 }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="phone">{t('hero.form.phone')} *</Form.Label>
                      <div className="phone-input-group-luxury">
                        <Form.Select
                          id="countryCode"
                          name="countryCode"
                          value={formData.countryCode}
                          onChange={handleInputChange}
                          required
                          className="form-control-hero phone-code-select"
                          aria-label={t('hero.form.countryCode')}
                        >
                          {PHONE_COUNTRY_OPTIONS.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.flag} {country.code}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control
                          type="tel"
                          id="phone"
                          name="phone"
                          placeholder={t('hero.form.phonePlaceholder')}
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                          minLength={8}
                          maxLength={12}
                          pattern="[1-9][0-9]{7,11}"
                          title={t('form.phoneValidation')}
                          className="form-control-hero phone-number-input"
                          autoComplete="tel"
                          inputMode="numeric"
                          style={{ pointerEvents: 'auto', zIndex: 10 }}
                        />
                      </div>
                      <Form.Text className="phone-hint-luxury">
                        {t('form.phoneHint')}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="email">{t('hero.form.email')} *</Form.Label>
                  <Form.Control
                    type="email"
                    id="email"
                    name="email"
                    placeholder="your.email@example.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="form-control-hero"
                    autoComplete="email"
                    style={{ pointerEvents: 'auto', zIndex: 10 }}
                  />
                </Form.Group>
                
                <Form.Group className="mb-4">
                  <Form.Label htmlFor="message">{t('hero.form.message')}</Form.Label>
                  <Form.Control
                    as="textarea"
                    id="message"
                    name="message"
                    placeholder={t('hero.form.messagePlaceholder')}
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="form-control-hero"
                    style={{ pointerEvents: 'auto', zIndex: 10 }}
                  />
                </Form.Group>
                
                {/* reCAPTCHA v2 Widget */}
                {recaptchaSiteKey && (
                  <div className="mb-3 d-flex justify-content-center">
                    <div ref={recaptchaRef}></div>
                  </div>
                )}
                
                <Button type="submit" className="hero-submit-btn-luxury" disabled={submitting}>
                  <span className="btn-text">
                    {submitting ? 'Gönderiliyor...' : t('hero.form.submit')}
                  </span>
                  {submitting ? (
                    <i className="fas fa-spinner fa-spin btn-icon"></i>
                  ) : (
                    <i className="fas fa-arrow-right btn-icon"></i>
                  )}
                </Button>
                
                <div className="form-footer">
                  <p>
                    <i className="fas fa-shield-alt"></i>
                    {t('hero.form.security')}
                  </p>
                </div>
              </Form>
            </div>
          </Col>

          {/* Doctor Image - Sağ (Desktop'ta sağ, mobilde önce) */}
          <Col lg={6} className="order-1 order-lg-2 hero-image-col">
            <div className="hero-image-container">
              <img 
                src={`${process.env.PUBLIC_URL}/assets/img/ayse-sezim-safak.webp`}
                alt="Doç. Dr. Ayşe Sezim"
                className="hero-doctor-image"
              />
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Hero;
